import {
  collection,
  documentId,
  getDocs,
  query,
  where,
  type DocumentData,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { normalizeInstagramHandle } from '@/lib/localGuestSpots'
import type { Artist, GuestSpot } from '@/types/models'

function parseArtistDocument(artistDocumentId: string, data: DocumentData): Artist {
  const createdAt = data.createdAt instanceof Timestamp
    ? data.createdAt.toDate()
    : new Date(data.createdAt)

  return {
    id: artistDocumentId,
    displayName: data.displayName || '',
    firstName: data.firstName,
    lastName: data.lastName,
    bio: data.bio,
    location: data.location || { city: '', country: '' },
    styles: data.styles || [],
    avatarUrl: data.avatarUrl,
    compressedAvatarUrl: data.compressedAvatarUrl,
    createdAt,
    isAvailable: data.isAvailable || false,
  }
}

export async function fetchPlatformArtistsForGuestSpots(guestSpots: GuestSpot[]): Promise<{
  artistsById: Map<string, Artist>
  artistsByInstagramHandle: Map<string, Artist>
}> {
  const artistIds = new Set<string>()
  const instagramHandles = new Set<string>()

  guestSpots.forEach((guestSpot) => {
    if (guestSpot.artistId) {
      artistIds.add(guestSpot.artistId)
    }

    const instagramHandle = normalizeInstagramHandle(guestSpot.username)
    if (instagramHandle) {
      instagramHandles.add(instagramHandle)
    }
  })

  const artistsById = new Map<string, Artist>()
  const artistsByInstagramHandle = new Map<string, Artist>()

  const artistIdList = [...artistIds]
  for (let batchStartIndex = 0; batchStartIndex < artistIdList.length; batchStartIndex += 30) {
    const artistIdBatch = artistIdList.slice(batchStartIndex, batchStartIndex + 30)
    const artistIdQuery = query(
      collection(db, 'artists'),
      where(documentId(), 'in', artistIdBatch),
    )
    const artistIdSnapshot = await getDocs(artistIdQuery)

    artistIdSnapshot.forEach((artistDocument) => {
      const artist = parseArtistDocument(artistDocument.id, artistDocument.data())
      artistsById.set(artist.id, artist)
    })
  }

  const instagramHandleList = [...instagramHandles]
  for (let batchStartIndex = 0; batchStartIndex < instagramHandleList.length; batchStartIndex += 30) {
    const instagramHandleBatch = instagramHandleList.slice(batchStartIndex, batchStartIndex + 30)
    const displayNameQuery = query(
      collection(db, 'artists'),
      where('displayName', 'in', instagramHandleBatch),
    )
    const displayNameSnapshot = await getDocs(displayNameQuery)

    displayNameSnapshot.forEach((artistDocument) => {
      const artist = parseArtistDocument(artistDocument.id, artistDocument.data())
      const normalizedDisplayName = normalizeInstagramHandle(artist.displayName)

      if (normalizedDisplayName) {
        artistsByInstagramHandle.set(normalizedDisplayName, artist)
      }
    })
  }

  return { artistsById, artistsByInstagramHandle }
}

export function resolvePlatformArtistForGuestSpot(
  guestSpot: GuestSpot,
  artistsById: Map<string, Artist>,
  artistsByInstagramHandle: Map<string, Artist>,
): Artist | undefined {
  if (guestSpot.artistId) {
    const artistById = artistsById.get(guestSpot.artistId)
    if (artistById) {
      return artistById
    }
  }

  const instagramHandle = normalizeInstagramHandle(guestSpot.username)
  if (!instagramHandle) {
    return undefined
  }

  return artistsByInstagramHandle.get(instagramHandle)
}
